import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './filesTab.module.scss';
import { useInfiniteQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { ActivityAssociation, File } from 'v2/models';
import InfiniteScroll from 'v2/common/InfiniteScroll/InfiniteScroll';
import ActivityItemAssociations from 'v2/common/DRM/ActivityTab/ActivityItemAssociations';
import NoAssociationsPlaceholder from 'v2/common/DRM/NoAssociationsPlaceholder/NoAssociationsPlaceholder';
import { PAGE_SIZE, FEATURE_FLAGS } from 'utils/constants';
import FileItem from './FileItems/FileItem';
import FilesGroupIcon from 'v2/images/files-group-icon.svg';
import CreateFileModal from './FileModals/CreateFileModal/CreateFileModal';
import AddAssociationButton from '../AddAssociationButton/AddAssociationButton';
import { BulkFilesModal } from './BulkFilesModal/BulkFilesModal';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';

interface IPage {
  count: number;
  next: number;
  prev: number;
  results: File[];
}

interface FileListProps {
  page: IPage;
  refetch: () => void;
  isFetching: boolean;
  defaultAssociations: ActivityAssociation[];
  isBulkDRMUploadEnabled: boolean;
  setShowCreateFileModal: (value: boolean) => void;
}

interface FilesQueryResult {
  count: number;
  results: File[];
}

export interface FilesTabProps {
  relatedTable: string;
  relatedObjectId: number;
  dataVendorId?: number;
  dataProductId?: number;
  primaryObjectType: 'DataVendor' | 'DataProduct';
  defaultAssociations: ActivityAssociation[];
}

const FileList = ({
  page,
  refetch,
  isFetching,
  defaultAssociations,
  isBulkDRMUploadEnabled,
  setShowCreateFileModal,
}: FileListProps) => {
  if (page.count === 0) {
    return (
      <div key={page.next}>
        <NoAssociationsPlaceholder
          onButtonClick={() => setShowCreateFileModal(true)}
          buttonLabel={isBulkDRMUploadEnabled ? 'Add files' : 'Add A file'}
          image={FilesGroupIcon}
          message={`You haven't added any files, add one now.`}
        />
      </div>
    );
  }

  return (
    <div key={page.next}>
      {page.results.map((file) => (
        <FileItem
          key={file.id}
          file={file}
          associationsComponent={
            <ActivityItemAssociations
              file={file}
              refetch={refetch}
              disabled={isFetching}
              defaultAssociations={defaultAssociations}
            />
          }
        />
      ))}
    </div>
  );
};

const FilesTab = ({
  relatedTable,
  relatedObjectId,
  dataVendorId,
  dataProductId,
  primaryObjectType,
  defaultAssociations,
}: FilesTabProps) => {
  const { flags } = useFeatureFlagContext();
  const isBulkDRMUploadEnabled = flags && flags[FEATURE_FLAGS.BULK_DRM_UPLOAD];
  const [showCreateFileModal, setShowCreateFileModal] = useState(false);
  const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery(
      ['FilesTabList', relatedTable],
      async ({ pageParam = 1 }) => {
        const res = await restFactory.get<FilesQueryResult>(
          `/dj/api/v1/file/`,
          {
            page: pageParam,
            size: PAGE_SIZE,
            relationships__table_name: relatedTable,
            relationships__object_id: relatedObjectId,
            ordering: '-timestamp',
          }
        );

        return {
          ...res,
          prev: pageParam,
          next: pageParam + 1,
        };
      },
      {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => {
          const hasMorePages = PAGE_SIZE * lastPage.prev < lastPage.count;
          return hasMorePages ? lastPage.next : undefined;
        },
      }
    );

  useEffect(() => {
    refetch();
  }, [relatedObjectId]);

  if (!data) {
    return null;
  }

  const handleOnCloseCreateModal = () => {
    setShowCreateFileModal(false);
    refetch();
  };

  return (
    <>
      {data.pages[0]?.count > 0 && (
        <div className={classnames(styles.controlPanel)}>
          <div>
            <AddAssociationButton
              onClick={() => {
                setShowCreateFileModal(true);
              }}
              label={isBulkDRMUploadEnabled ? 'Add files' : 'Add File'}
            />
          </div>
        </div>
      )}
      <div className={classnames(styles.fileListContainer)}>
        {data.pages.map((page) => (
          <FileList
            key={page.next}
            page={page}
            refetch={refetch}
            isFetching={isFetching}
            defaultAssociations={defaultAssociations}
            isBulkDRMUploadEnabled={isBulkDRMUploadEnabled}
            setShowCreateFileModal={setShowCreateFileModal}
          />
        ))}
      </div>
      <InfiniteScroll
        callback={() => fetchNextPage()}
        isLoading={isFetching}
        disabled={!hasNextPage}
        scrollThreshold={500}
      />

      {isBulkDRMUploadEnabled
        ? showCreateFileModal && (
            <BulkFilesModal
              onClose={handleOnCloseCreateModal}
              primaryObjectType={primaryObjectType}
              {...(dataVendorId && { dataVendorId: dataVendorId })}
              {...(dataProductId && { dataProductId: dataProductId })}
            />
          )
        : showCreateFileModal && (
            <CreateFileModal
              onClose={handleOnCloseCreateModal}
              defaultAssociations={defaultAssociations}
            />
          )}
    </>
  );
};

export default FilesTab;
